import {
    Alert,
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { INPUT_VALIDATION } from "../../constants";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import { PROPERTY_TYPES } from "./constants";
import slugify from "slugify";
import { useCrops } from "../../hooks/swr";
import { useSnackbar } from "../../hooks";

export const ParcellaireAddProperty = ({
    onClose,
    parcellaireState,
    parcellaireDispatch,
    setGeoJsonKey,
    setSelectableMetadata,
}) => {
    const { openSnackbar } = useSnackbar();

    const { data: cropsData } = useCrops();
    const crops = cropsData?.rows || [];

    const { control, handleSubmit, watch, register, reset } = useForm({
        defaultValues: {
            propertyType: PROPERTY_TYPES.CROP.value,
            key: "",
            value: "",
        },
    });

    const [propertyTypeWatch, keyWatch, valueWatch] = watch([
        "propertyType",
        "key",
        "value",
    ]);

    const propertyTypeObject = Object.values(PROPERTY_TYPES).find(
        (property) => property.value === propertyTypeWatch
    );

    const addMetadataToSelectableList = (newMetadata) => {
        if (newMetadata && setSelectableMetadata) {
            setSelectableMetadata((prev) => {
                const existingIds = new Set(prev.map((item) => item.id));
                if (!existingIds.has(newMetadata)) {
                    return [...prev, { label: newMetadata, id: newMetadata }];
                }
                return prev;
            });
        }
    };

    const onSubmit = (payload) => {
        if (parcellaireState.selectedLayers.length === 0) {
            return;
        }

        const propertyKey =
            payload.propertyType === PROPERTY_TYPES.CUSTOM.value
                ? slugify(payload.key, {
                      replacement: "_",
                      strict: true,
                      lower: true,
                  })
                : payload.propertyType;
        const propertyValue = payload.value;

        if (propertyKey === "plot_id") {
            openSnackbar(
                "The 'plot_id' field should not be modified directly. If you wish to modify the field containing the client's plot_id, you should modify 'client_id' instead.",
                "error"
            );
            return;
        }

        const shouldRegeneratePlotId =
            propertyKey === "experiment" ||
            propertyKey === "x" ||
            propertyKey === "y";

        if (shouldRegeneratePlotId) {
            parcellaireDispatch({
                type: PARCELLAIRE_REDUCER_ACTIONS.ADD_PROPERTY_WITH_PLOT_ID_REGENERATION,
                propertyKey,
                propertyValue,
                slugify,
            });
        } else {
            parcellaireDispatch({
                type: PARCELLAIRE_REDUCER_ACTIONS.ADD_PROPERTY,
                propertyKey,
                propertyValue,
            });
        }

        setGeoJsonKey(new Date().getTime());

        addMetadataToSelectableList(propertyKey);

        onClose();
    };

    const selectedCount = parcellaireState.selectedLayers.length;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
                Add Property to Selected Feature
                {selectedCount > 1 && `s`}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        Adding a property that already exists on a feature will
                        overwrite the previous data.
                    </Typography>
                </Alert>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Controller
                        control={control}
                        name="propertyType"
                        render={({ field: { onChange, ...fieldProps } }) => (
                            <TextField
                                {...fieldProps}
                                select
                                label="Property"
                                fullWidth
                                onChange={(e) => {
                                    onChange(e);
                                    reset((formValues) => ({
                                        ...formValues,
                                        value: "",
                                        key: "",
                                    }));
                                }}
                            >
                                {Object.values(PROPERTY_TYPES).map(
                                    (propertyType) => (
                                        <MenuItem
                                            key={propertyType.value}
                                            value={propertyType.value}
                                        >
                                            {propertyType.label}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        )}
                    />

                    {propertyTypeWatch === PROPERTY_TYPES.CUSTOM.value && (
                        <Controller
                            control={control}
                            name="key"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Property Name"
                                    fullWidth
                                />
                            )}
                        />
                    )}

                    {propertyTypeObject.type === "crop" ? (
                        <Controller
                            control={control}
                            name="value"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    options={crops}
                                    getOptionLabel={(option) => option.name}
                                    value={
                                        crops.find(
                                            (crop) => crop.name === valueWatch
                                        ) || null
                                    }
                                    onChange={(_, newValue) => {
                                        onChange(newValue ? newValue.name : "");
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Value"
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value?.uuid
                                    }
                                />
                            )}
                        />
                    ) : (
                        <TextField
                            name="value"
                            label="Value"
                            fullWidth
                            {...register("value", {
                                required: INPUT_VALIDATION.REQUIRED,
                                valueAsNumber:
                                    propertyTypeObject.type === "float",
                                validate:
                                    propertyTypeObject.type === "float"
                                        ? (value) =>
                                              value >= 0 ||
                                              INPUT_VALIDATION.POSITIVE
                                        : undefined,
                            })}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={
                        (propertyTypeWatch === PROPERTY_TYPES.CUSTOM &&
                            !keyWatch) ||
                        (propertyTypeWatch !== PROPERTY_TYPES.CUSTOM &&
                            !valueWatch)
                    }
                >
                    Apply to {selectedCount} feature
                    {selectedCount !== 1 ? "s" : ""}
                </Button>
            </DialogActions>
        </form>
    );
};
